<template>
  <CustomImageUpload
    v-if="uploadType === 'image'"
    v-bind="$attrs"
    v-model="fileList"
    :file-key="fileKey"
    :accept="accept"
    :scene="scene"
    :isFileUrl="isFileUrl"
    :uploadBtnTitle="uploadBtnTitle"
    @upload-progress="$emit('upload-progress', $event)"
    @success="$emit('success', $event)"
    @error="$emit('error', $event)"
    @remove="$emit('remove', $event)"
    ref="uploadComponentRef"
  />

  <CustomDocUpload
    v-else
    v-bind="$attrs"
    v-model="fileList"
    :file-key="fileKey"
    :accept="accept"
    :scene="scene"
    :isFileUrl="isFileUrl"
    :uploadBtnTitle="uploadBtnTitle"
    @upload-progress="$emit('upload-progress', $event)"
    @success="$emit('success', $event)"
    @error="$emit('error', $event)"
    @remove="$emit('remove', $event)"
    ref="uploadComponentRef"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CustomDocUpload from './CustomDocUpload.vue'
import CustomImageUpload from './CustomImageUpload.vue'

// ✅ 核心：接收【所有统一的props】，动态表单里传什么就接什么，无需区分类型
const props = defineProps({
  // 所有上传组件的公共属性，全部兼容
  fileKey: { type: String, default: 'default' },
  scene: { type: String, default: 'attachment' },
  accept: { type: String, default: '' },
  isFileUrl: { type: Boolean, default: false },
  uploadBtnTitle: { type: String, default: '上传文件' },
  modelValue: { type: Array, default: () => [] }
})

// ✅ 核心：通过accept自动判断上传类型【重中之重】
const uploadType = computed(() => {
  const accept = props.accept.toLowerCase()
  // 规则：只要accept包含 图片相关标识 → 图片上传组件
  if (
    accept.includes('image/*') ||
    accept.includes('.jpg') ||
    accept.includes('.jpeg') ||
    accept.includes('.png') ||
    accept.includes('.webp')
  ) {
    return 'image'
  }
  // 否则全部走文档上传组件（pdf/word/excel/zip等）
  return 'file'
})

// 统一的v-model双向绑定
const emit = defineEmits([
  'update:modelValue',
  'upload-progress',
  'success',
  'error',
  'remove'
])
const fileList = ref(props.modelValue)
watch(fileList, (val) => emit('update:modelValue', val), { deep: true })
watch(
  () => props.modelValue,
  (val) => (fileList.value = val),
  { deep: true }
)

const uploadComponentRef = ref()

// 对外暴露所有上传组件需要的方法，统一调用，无需区分类型
defineExpose({
  // 手动触发上传 → 你之前BUG的核心方法，现在完美调用
  triggerUpload: async () => {
    // 等待组件挂载
    await nextTick()
    if (uploadComponentRef.value?.triggerUpload) {
      uploadComponentRef.value.triggerUpload()
    }
  },
  // 清空上传文件
  clearUpload: () => uploadComponentRef.value?.clearUpload(),
  // 获取文件列表
  getFileList: () => uploadComponentRef.value?.getFileList() || [],
  // 暴露内部ref，兜底兼容
  uploadRef: uploadComponentRef.value?.uploadRef
})
</script>
